// Symbolic integration engine for Bangla Calculator.
// Implements a rule-based recursive integrator over mathjs AST.
// Handles: constants, power rule, 1/x, exp, a^x, sin/cos/tan, sec²/csc²,
// linearity (sums/differences), constant multiples, and basic linear
// u-substitution for f(ax+b).
//
// Falls back to numerical Simpson's rule for definite integrals when
// symbolic integration fails.

import { parse, simplify, evaluate, type MathNode } from "mathjs";

export interface IntegrationStep {
  en: string;
  bn: string;
  expr?: string; // mathjs expression that can be rendered with KaTeX
}

export interface IntegrationResult {
  ok: boolean;
  /** Symbolic antiderivative (mathjs syntax) without "+ C". */
  antiderivative?: string;
  /** Same, with "+ C" appended for indefinite display. */
  display?: string;
  /** Display LaTeX. */
  displayTex?: string;
  /** True if numerical fallback was used. */
  numerical?: boolean;
  /** Numerical value for definite integrals. */
  value?: number;
  steps: IntegrationStep[];
  error?: { en: string; bn: string };
}

// ---------------------------------------------------------------------------
// Input normalisation
// ---------------------------------------------------------------------------
const SUPER_MAP: Record<string, string> = {
  "²": "^2", "³": "^3", "⁴": "^4", "⁵": "^5", "⁶": "^6",
  "⁷": "^7", "⁸": "^8", "⁹": "^9", "⁰": "^0", "¹": "^1",
};

export function normalizeExpression(input: string): string {
  let s = input.trim();
  // strip leading "y =" or "f(x) ="
  s = s.replace(/^\s*y\s*=\s*/i, "");
  s = s.replace(/^\s*f\s*\(\s*[a-z]\s*\)\s*=\s*/i, "");
  // unicode supers and operators
  s = s.replace(/[²³⁴⁵⁶⁷⁸⁹⁰¹]/g, (c) => SUPER_MAP[c] ?? c);
  s = s.replace(/×/g, "*").replace(/÷/g, "/");
  s = s.replace(/−/g, "-").replace(/–/g, "-").replace(/—/g, "-");
  // π
  s = s.replace(/π/g, "pi");
  // Implicit multiplication for things like 2x → 2*x, 3sin(x) → 3*sin(x), but NOT for exp(...)
  // mathjs parser already supports implicit mult; leave as-is.
  return s;
}

// ---------------------------------------------------------------------------
// AST helpers
// ---------------------------------------------------------------------------
type Node = MathNode;

const PARSE_OK = (s: string): Node | null => { try { return parse(s); } catch { return null; } };

function isConstant(node: Node, v: string): boolean {
  let constant = true;
  node.traverse((n) => {
    if (n.type === "SymbolNode" && (n as any).name === v) constant = false;
  });
  return constant;
}

function isVar(node: Node, v: string): boolean {
  return node.type === "SymbolNode" && (node as any).name === v;
}

function nodeToString(node: Node): string {
  return node.toString({ parenthesis: "auto", implicit: "show" });
}

function trySimplify(s: string): string {
  try {
    const n = simplify(s);
    return nodeToString(n);
  } catch { return s; }
}

function trySimplifyTex(s: string): string {
  try {
    const n = simplify(s);
    return n.toTex({ parenthesis: "auto" });
  } catch {
    try { return parse(s).toTex({ parenthesis: "auto" }); }
    catch { return s; }
  }
}

/** Numerically evaluate a node treated as a constant in v (returns NaN if depends on v or fails). */
function evalConstNumber(node: Node, v: string): number {
  if (!isConstant(node, v)) return NaN;
  try {
    const val = node.evaluate({});
    return typeof val === "number" ? val : NaN;
  } catch { return NaN; }
}

// ---------------------------------------------------------------------------
// Linear-in-v detection for u-substitution f(ax+b)
// ---------------------------------------------------------------------------
interface LinearForm { a: number; b: number; }

function asLinear(node: Node, v: string): LinearForm | null {
  // Returns {a,b} if node = a*v + b numerically, otherwise null.
  // Strategy: evaluate at v=0 (=> b), and at v=1 (=> a+b), require both finite
  // and require that node's symbolic form contains v at most linearly. We check
  // by evaluating at v=2 to confirm a+b + a = 2a+b matches.
  if (!containsVar(node, v)) return null;
  try {
    const f0 = node.evaluate({ [v]: 0 });
    const f1 = node.evaluate({ [v]: 1 });
    const f2 = node.evaluate({ [v]: 2 });
    if (typeof f0 !== "number" || typeof f1 !== "number" || typeof f2 !== "number") return null;
    const a = f1 - f0;
    const b = f0;
    if (Math.abs((2 * a + b) - f2) > 1e-9 * Math.max(1, Math.abs(f2))) return null;
    if (!Number.isFinite(a) || !Number.isFinite(b) || a === 0) return null;
    return { a, b };
  } catch { return null; }
}

function containsVar(node: Node, v: string): boolean {
  let yes = false;
  node.traverse((n) => {
    if (n.type === "SymbolNode" && (n as any).name === v) yes = true;
  });
  return yes;
}

function fmtNum(n: number): string {
  if (Number.isInteger(n)) return n.toString();
  // try simple fraction
  const sign = n < 0 ? "-" : "";
  const a = Math.abs(n);
  for (let d = 2; d <= 12; d++) {
    const num = a * d;
    if (Math.abs(num - Math.round(num)) < 1e-9) {
      return `${sign}${Math.round(num)}/${d}`;
    }
  }
  return n.toString();
}

// ---------------------------------------------------------------------------
// Core integrator
// ---------------------------------------------------------------------------
interface IntegrateOk { ok: true; expr: string; rule: { en: string; bn: string } }
interface IntegrateFail { ok: false }
type IntegrateOut = IntegrateOk | IntegrateFail;

const FAIL: IntegrateFail = { ok: false };

function ok(expr: string, en: string, bn: string): IntegrateOk {
  return { ok: true, expr, rule: { en, bn } };
}

/** Integrate node with respect to variable v. Returns mathjs expression string. */
function integrateNode(node: Node, v: string): IntegrateOut {
  // 1) Constant
  if (isConstant(node, v)) {
    const c = nodeToString(node);
    return ok(`(${c}) * ${v}`,
      `Integrate constant ${c}: ∫${c} d${v} = ${c}·${v}`,
      `ধ্রুবক ${c} এর অখণ্ডন: ∫${c} d${v} = ${c}·${v}`);
  }

  // 2) The variable itself
  if (isVar(node, v)) {
    return ok(`${v}^2 / 2`,
      `Power rule with n=1: ∫${v} d${v} = ${v}²/2`,
      `পাওয়ার রুল n=1: ∫${v} d${v} = ${v}²/2`);
  }

  // 3) Sum / difference (linearity)
  if (node.type === "OperatorNode" && ((node as any).op === "+" || (node as any).op === "-") && (node as any).args.length === 2) {
    const args = (node as any).args as Node[];
    const op = (node as any).op as "+" | "-";
    const left = integrateNode(args[0], v);
    const right = integrateNode(args[1], v);
    if (left.ok && right.ok) {
      return ok(`(${left.expr}) ${op} (${right.expr})`,
        `Linearity: ∫(f ${op} g) d${v} = ∫f d${v} ${op} ∫g d${v}`,
        `যোগফল-বিভেদ নিয়ম: ∫(f ${op} g) d${v} = ∫f d${v} ${op} ∫g d${v}`);
    }
    return FAIL;
  }

  // unary minus
  if (node.type === "OperatorNode" && (node as any).op === "-" && (node as any).args.length === 1) {
    const inner = integrateNode((node as any).args[0], v);
    if (inner.ok) return ok(`-(${inner.expr})`,
      `Constant multiple −1 pulled out`,
      `−১ ধ্রুবক বের করা হলো`);
    return FAIL;
  }

  // 4) Constant multiple: c * f(x)  or  f(x) * c
  if (node.type === "OperatorNode" && (node as any).op === "*" && (node as any).args.length === 2) {
    const [a, b] = (node as any).args as [Node, Node];
    let cnode: Node | null = null, fnode: Node | null = null;
    if (isConstant(a, v) && containsVar(b, v)) { cnode = a; fnode = b; }
    else if (isConstant(b, v) && containsVar(a, v)) { cnode = b; fnode = a; }
    if (cnode && fnode) {
      const inner = integrateNode(fnode, v);
      if (inner.ok) {
        const c = nodeToString(cnode);
        return ok(`(${c}) * (${inner.expr})`,
          `Constant multiple rule: ∫${c}·f(${v}) d${v} = ${c}·∫f(${v}) d${v}`,
          `ধ্রুবক গুণনিয়ম: ∫${c}·f(${v}) d${v} = ${c}·∫f(${v}) d${v}`);
      }
      return FAIL;
    }
  }

  // 5) Quotient: (numerator) / (denominator)
  if (node.type === "OperatorNode" && (node as any).op === "/" && (node as any).args.length === 2) {
    const [num, den] = (node as any).args as [Node, Node];
    // c / g(x): treat as c * g(x)^-1
    if (isConstant(num, v) && containsVar(den, v)) {
      const c = nodeToString(num);
      // 1 / x → ln|x|
      if (isVar(den, v)) {
        return ok(`(${c}) * log(abs(${v}))`,
          `Reciprocal rule: ∫${c}/${v} d${v} = ${c}·ln|${v}|`,
          `প্রতিলোম সূত্র: ∫${c}/${v} d${v} = ${c}·ln|${v}|`);
      }
      // 1/(ax+b)
      const lin = asLinear(den, v);
      if (lin) {
        const cVal = evalConstNumber(num, v);
        const factor = Number.isFinite(cVal) ? fmtNum(cVal / lin.a) : `(${c})/${fmtNum(lin.a)}`;
        return ok(`(${factor}) * log(abs(${nodeToString(den)}))`,
          `Linear u-sub: ∫1/(${fmtNum(lin.a)}${v}+${fmtNum(lin.b)}) d${v} = (1/${fmtNum(lin.a)})·ln|${fmtNum(lin.a)}${v}+${fmtNum(lin.b)}|`,
          `রৈখিক u-প্রতিস্থাপন: ∫1/(ax+b) d${v} = (1/a)·ln|ax+b|`);
      }
    }
    // f(x)/c where c is constant → factor c out
    if (containsVar(num, v) && isConstant(den, v)) {
      const inner = integrateNode(num, v);
      if (inner.ok) {
        const c = nodeToString(den);
        return ok(`(${inner.expr}) / (${c})`,
          `Constant denominator factored out`,
          `ধ্রুবক হর বের করা হলো`);
      }
    }
  }

  // 6) Power: f(x)^n
  if (node.type === "OperatorNode" && (node as any).op === "^" && (node as any).args.length === 2) {
    const [base, exponent] = (node as any).args as [Node, Node];
    // x^n where n is constant
    if (isVar(base, v) && isConstant(exponent, v)) {
      const nVal = evalConstNumber(exponent, v);
      if (Number.isFinite(nVal)) {
        if (Math.abs(nVal + 1) < 1e-12) {
          return ok(`log(abs(${v}))`,
            `Special case n = −1: ∫${v}⁻¹ d${v} = ln|${v}|`,
            `বিশেষ ক্ষেত্র n = −১: ∫${v}⁻¹ d${v} = ln|${v}|`);
        }
        const np1 = nVal + 1;
        return ok(`${v}^(${fmtNum(np1)}) / (${fmtNum(np1)})`,
          `Power rule: ∫${v}^${fmtNum(nVal)} d${v} = ${v}^${fmtNum(np1)}/${fmtNum(np1)}`,
          `পাওয়ার রুল: ∫${v}^${fmtNum(nVal)} d${v} = ${v}^${fmtNum(np1)}/${fmtNum(np1)}`);
      }
    }
    // (ax+b)^n
    const lin = asLinear(base, v);
    if (lin && isConstant(exponent, v)) {
      const nVal = evalConstNumber(exponent, v);
      if (Number.isFinite(nVal)) {
        if (Math.abs(nVal + 1) < 1e-12) {
          return ok(`(1/${fmtNum(lin.a)}) * log(abs(${nodeToString(base)}))`,
            `Linear u-sub for n=−1`,
            `n=−১ এর জন্য রৈখিক u-প্রতিস্থাপন`);
        }
        const np1 = nVal + 1;
        return ok(`(${nodeToString(base)})^(${fmtNum(np1)}) / (${fmtNum(lin.a * np1)})`,
          `Linear u-sub: ∫(a${v}+b)^n d${v} = (a${v}+b)^(n+1)/(a(n+1))`,
          `রৈখিক u-প্রতিস্থাপন: ∫(ax+b)^n d${v} = (ax+b)^(n+1)/(a(n+1))`);
      }
    }
    // a^x where a constant, exponent linear in v
    if (isConstant(base, v) && containsVar(exponent, v)) {
      const lin = asLinear(exponent, v);
      const baseStr = nodeToString(base);
      if (lin) {
        // ∫a^(αx+β) dx = a^(αx+β) / (α·ln(a))
        return ok(`(${baseStr})^(${nodeToString(exponent)}) / (${fmtNum(lin.a)} * log(${baseStr}))`,
          `Exponential rule: ∫a^(${v}) d${v} = a^${v}/ln(a)`,
          `সূচকীয় সূত্র: ∫a^(${v}) d${v} = a^${v}/ln(a)`);
      }
    }
  }

  // 7) Function calls: sin, cos, tan, exp, log, ln, sec, csc, cot, sqrt
  if (node.type === "FunctionNode") {
    const name = (node as any).name as string;
    const args = (node as any).args as Node[];
    if (args.length === 1) {
      const arg = args[0];
      // ∫sqrt(x) dx → ∫x^(1/2) dx — let power rule do it via simplify rewrite
      if (name === "sqrt" && containsVar(arg, v)) {
        // Rewrite sqrt(arg) as arg^(1/2) and recurse
        const rewritten = PARSE_OK(`(${nodeToString(arg)})^(1/2)`);
        if (rewritten) return integrateNode(rewritten, v);
      }
      // exp(g(x)) where g linear
      if (name === "exp" && containsVar(arg, v)) {
        const lin = asLinear(arg, v);
        if (lin) {
          return ok(`exp(${nodeToString(arg)}) / (${fmtNum(lin.a)})`,
            `Exponential rule: ∫e^(${v}) d${v} = e^${v}` ,
            `সূচকীয় সূত্র: ∫e^(${v}) d${v} = e^${v}`);
        }
      }
      // log/ln: skip (would need integration by parts) — handled by IBP fallback below
      const linArg = containsVar(arg, v) ? asLinear(arg, v) : null;
      const trigMap: Record<string, (a: string, ainv: string) => { expr: string; en: string; bn: string }> = {
        sin: (a, ainv) => ({
          expr: `-cos(${a}) * (${ainv})`,
          en: `∫sin(${v}) d${v} = −cos(${v})`, bn: `∫sin(${v}) d${v} = −cos(${v})`,
        }),
        cos: (a, ainv) => ({
          expr: `sin(${a}) * (${ainv})`,
          en: `∫cos(${v}) d${v} = sin(${v})`, bn: `∫cos(${v}) d${v} = sin(${v})`,
        }),
        tan: (a, ainv) => ({
          expr: `-log(abs(cos(${a}))) * (${ainv})`,
          en: `∫tan(${v}) d${v} = −ln|cos(${v})|`, bn: `∫tan(${v}) d${v} = −ln|cos(${v})|`,
        }),
        cot: (a, ainv) => ({
          expr: `log(abs(sin(${a}))) * (${ainv})`,
          en: `∫cot(${v}) d${v} = ln|sin(${v})|`, bn: `∫cot(${v}) d${v} = ln|sin(${v})|`,
        }),
        sec: (a, ainv) => ({
          expr: `log(abs(sec(${a}) + tan(${a}))) * (${ainv})`,
          en: `∫sec(${v}) d${v} = ln|sec(${v}) + tan(${v})|`, bn: `∫sec(${v}) d${v} = ln|sec(${v}) + tan(${v})|`,
        }),
        csc: (a, ainv) => ({
          expr: `-log(abs(csc(${a}) + cot(${a}))) * (${ainv})`,
          en: `∫csc(${v}) d${v} = −ln|csc(${v}) + cot(${v})|`, bn: `∫csc(${v}) d${v} = −ln|csc(${v}) + cot(${v})|`,
        }),
      };
      if (trigMap[name] && containsVar(arg, v)) {
        if (isVar(arg, v)) {
          const r = trigMap[name](v, "1");
          return ok(r.expr, r.en, r.bn);
        }
        if (linArg) {
          const r = trigMap[name](nodeToString(arg), `1/${fmtNum(linArg.a)}`);
          return ok(r.expr, `Linear u-sub. ${r.en}`, `রৈখিক u-প্রতিস্থাপন। ${r.bn}`);
        }
      }
      // log10(x), log(x) (mathjs log = natural)
      if ((name === "log" || name === "ln") && isVar(arg, v)) {
        // ∫ln(x) dx = x ln(x) − x
        return ok(`${v} * log(${v}) - ${v}`,
          `Integration by parts: ∫ln(${v}) d${v} = ${v}·ln(${v}) − ${v}`,
          `অংশভাগে অখণ্ডন: ∫ln(${v}) d${v} = ${v}·ln(${v}) − ${v}`);
      }
    }
  }

  // 8) Implicit constant if node has no v (defensive)
  if (!containsVar(node, v)) {
    return ok(`(${nodeToString(node)}) * ${v}`,
      `No ${v} found — treat as constant`,
      `${v} নেই — ধ্রুবক ধরা হলো`);
  }

  return FAIL;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface IntegrateOpts {
  variable?: string;
  /** If set, compute definite integral from a to b. */
  a?: number;
  b?: number;
}

export function integrate(input: string, opts: IntegrateOpts = {}): IntegrationResult {
  const v = opts.variable || "x";
  const norm = normalizeExpression(input);
  const steps: IntegrationStep[] = [];
  let parsed: Node;
  try {
    parsed = parse(norm);
  } catch (err) {
    return {
      ok: false,
      steps,
      error: { en: "Could not parse expression. Check syntax.", bn: "এক্সপ্রেশন পার্স করা যায়নি। সিনট্যাক্স যাচাই করুন।" },
    };
  }

  steps.push({
    en: `Integrate ∫ ${norm} d${v}`,
    bn: `অখণ্ডন ∫ ${norm} d${v}`,
    expr: `\\int ${parsed.toTex()} \\, d${v}`,
  });

  let simplified: Node = parsed;
  try { simplified = simplify(parsed); } catch {}

  const symbolic = integrateNode(simplified, v);

  if (symbolic.ok) {
    const rawAntideriv = symbolic.expr;
    let antiderivative = rawAntideriv;
    let antiTex = "";
    try {
      const simp = simplify(rawAntideriv);
      antiderivative = nodeToString(simp);
      antiTex = simp.toTex({ parenthesis: "auto" });
    } catch {
      try { antiTex = parse(rawAntideriv).toTex({ parenthesis: "auto" }); } catch { antiTex = rawAntideriv; }
    }

    steps.push({
      en: symbolic.rule.en,
      bn: symbolic.rule.bn,
    });
    steps.push({
      en: `Antiderivative F(${v}) = ${antiderivative}`,
      bn: `অখণ্ডিত F(${v}) = ${antiderivative}`,
      expr: `F(${v}) = ${antiTex}`,
    });

    // Indefinite
    if (opts.a === undefined || opts.b === undefined) {
      return {
        ok: true,
        antiderivative,
        display: `${antiderivative} + C`,
        displayTex: `${antiTex} + C`,
        steps,
      };
    }

    // Definite — F(b) - F(a)
    const { a, b } = opts;
    let Fb: number, Fa: number;
    try {
      Fb = Number(evaluate(antiderivative, { [v]: b }));
      Fa = Number(evaluate(antiderivative, { [v]: a }));
    } catch {
      // fallback to numeric
      const numeric = simpsonIntegrate(norm, v, a, b);
      steps.push({
        en: `Symbolic evaluation failed — using Simpson's rule.`,
        bn: `প্রতীকী মান নির্ণয় ব্যর্থ — সিম্পসন'স রুল ব্যবহার করা হলো।`,
      });
      return numericResult(numeric, a, b, steps);
    }
    if (!Number.isFinite(Fb) || !Number.isFinite(Fa)) {
      const numeric = simpsonIntegrate(norm, v, a, b);
      steps.push({
        en: `Antiderivative undefined at endpoints — using numerical Simpson's rule.`,
        bn: `প্রান্তবিন্দুতে অসংজ্ঞায়িত — সিম্পসন'স রুল ব্যবহার করা হলো।`,
      });
      return numericResult(numeric, a, b, steps);
    }
    const value = Fb - Fa;
    steps.push({
      en: `Apply Fundamental Theorem of Calculus: F(${b}) − F(${a})`,
      bn: `ক্যালকুলাসের মৌলিক উপপাদ্য প্রয়োগ: F(${b}) − F(${a})`,
      expr: `\\left[ ${antiTex} \\right]_{${a}}^{${b}}`,
    });
    steps.push({
      en: `= ${roundNice(Fb)} − ${roundNice(Fa)} = ${roundNice(value)}`,
      bn: `= ${roundNice(Fb)} − ${roundNice(Fa)} = ${roundNice(value)}`,
    });
    return {
      ok: true,
      antiderivative,
      display: roundNice(value),
      displayTex: roundNice(value),
      value,
      steps,
    };
  }

  // Symbolic failed
  if (opts.a !== undefined && opts.b !== undefined) {
    steps.push({
      en: `Symbolic integration failed — falling back to Simpson's rule (numerical).`,
      bn: `প্রতীকী অখণ্ডন ব্যর্থ — সিম্পসন'স রুল (সংখ্যাত্মক) ব্যবহার করা হলো।`,
    });
    const numeric = simpsonIntegrate(norm, v, opts.a, opts.b);
    return numericResult(numeric, opts.a, opts.b, steps);
  }

  return {
    ok: false,
    steps,
    error: {
      en: "Could not solve symbolically. Try a simpler form, or supply limits to use numerical integration.",
      bn: "প্রতীকীভাবে সমাধান করা যায়নি। সরল রূপে চেষ্টা করুন, অথবা সীমা দিন (সংখ্যাত্মক অখণ্ডনের জন্য)।",
    },
  };
}

function numericResult(value: number, a: number, b: number, steps: IntegrationStep[]): IntegrationResult {
  if (!Number.isFinite(value)) {
    return {
      ok: false,
      steps,
      error: { en: "Numerical integration failed (function may diverge).", bn: "সংখ্যাত্মক অখণ্ডন ব্যর্থ (ফাংশন অপসারী হতে পারে)।" },
    };
  }
  steps.push({
    en: `Simpson's rule with n=1000 over [${a}, ${b}] ≈ ${roundNice(value)}`,
    bn: `[${a}, ${b}] এ সিম্পসন'স রুল (n=১০০০) ≈ ${roundNice(value)}`,
  });
  return {
    ok: true,
    numerical: true,
    value,
    display: roundNice(value),
    displayTex: roundNice(value),
    steps,
  };
}

/** Composite Simpson's rule. Returns NaN if evaluation fails. */
export function simpsonIntegrate(exprStr: string, v: string, a: number, b: number, n = 1000): number {
  if (a === b) return 0;
  if (n % 2 !== 0) n++;
  let parsed: Node;
  try { parsed = parse(normalizeExpression(exprStr)); } catch { return NaN; }
  const compiled = parsed.compile();
  const f = (x: number): number => {
    try {
      const y = compiled.evaluate({ [v]: x });
      return typeof y === "number" && Number.isFinite(y) ? y : NaN;
    } catch { return NaN; }
  };
  const h = (b - a) / n;
  let sum = f(a) + f(b);
  if (!Number.isFinite(sum)) return NaN;
  for (let i = 1; i < n; i++) {
    const x = a + i * h;
    const y = f(x);
    if (!Number.isFinite(y)) return NaN;
    sum += (i % 2 === 0 ? 2 : 4) * y;
  }
  return (h / 3) * sum;
}

function roundNice(n: number): string {
  if (!Number.isFinite(n)) return "—";
  if (Math.abs(n - Math.round(n)) < 1e-9) return Math.round(n).toString();
  // attempt simple fraction
  const frac = fmtNum(n);
  if (/^-?\d+\/\d+$/.test(frac)) return frac;
  return Number(n.toFixed(6)).toString();
}

/** Render the integrand as TeX for display. */
export function exprToTex(input: string): string {
  try {
    return parse(normalizeExpression(input)).toTex({ parenthesis: "auto" });
  } catch { return input; }
}
